import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, ArrowLeft, BookOpen, Mail, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { EditableText } from "../lib/content";
import { useSeo } from "../lib/useSeo";

const focusSlug = (s) =>
  (s || "General").trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "general";
const focusLabel = (s) => (s || "General").trim();

// A single article card (used across featured + grid).
function ArticleCard({ p, testid }) {
  return (
    <Link key={p.id} to={`/insights/${p.slug}`} className="bnp-card overflow-hidden group flex flex-col" data-testid={testid}>
      {p.cover_image && (
        <div className="h-40 overflow-hidden">
          <img src={p.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
        <span className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">{focusLabel(p.category)} · {p.read_minutes || 5} min</span>
        <h3 className="font-display text-lg text-[#04273d] mt-1.5 font-semibold leading-snug">{p.title}</h3>
        <p className="text-[#447694] text-sm mt-2 leading-relaxed line-clamp-2">{p.excerpt}</p>
      </div>
    </Link>
  );
}

export default function InsightsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const activeFocus = params.get("focus") || "all";

  useSeo({
    title: "Blogs & Guidance - UK Business Name Protection & IP Advice",
    description: "Plain-English blogs and guidance for UK small businesses on protecting your trading name, passing off and trade marks. Browse by focus area.",
  });

  useEffect(() => {
    api.get("/insights").then((r) => setItems(r.data || [])).finally(() => setLoading(false));
  }, []);

  // Distinct focus areas built from the article categories (trimmed + de-duped).
  const focuses = useMemo(() => {
    const map = new Map();
    items.forEach((p) => {
      const slug = focusSlug(p.category);
      const cur = map.get(slug) || { slug, label: focusLabel(p.category), items: [] };
      cur.items.push(p);
      map.set(slug, cur);
    });
    return [...map.values()].sort((a, b) => b.items.length - a.items.length || a.label.localeCompare(b.label));
  }, [items]);

  const setFocus = (slug) => {
    const next = new URLSearchParams(params);
    if (slug === "all") next.delete("focus");
    else next.set("focus", slug);
    setParams(next);
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/leads", { email, source: "newsletter" });
      toast.success("Thanks. You're on the list.");
      setEmail("");
    } catch {
      toast.error("Could not sign up. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const featured = items[0];
  const activeGroup = focuses.find((f) => f.slug === activeFocus);
  const isAll = activeFocus === "all";

  return (
    <main data-testid="insights-list-page">
      <section className="relative overflow-hidden bnp-radial-glow pt-14 md:pt-20 pb-8">
        <div className="absolute inset-0 bnp-grid-bg opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 relative">
          <div className="text-center max-w-3xl mx-auto">
            <span className="bnp-chip bnp-chip-teal">Blogs &amp; Guidance</span>
            <h1 className="font-display text-[clamp(2rem,4vw,3.4rem)] text-[#04273d] mt-4 font-semibold tracking-tight leading-[1.05]">
              <EditableText keyName="insights.hero.title" default="Straightforward guidance on protecting your business name." />
            </h1>
            <p className="text-[#447694] text-lg mt-5 leading-relaxed">
              <EditableText keyName="insights.hero.body" default="Clear, practical blogs and guidance to help you understand your options, reduce risk, and protect what you are building. Written for business owners, not lawyers." />
            </p>
          </div>
        </div>
      </section>

      {/* FOCUS HEADERS - clickable filter by focus area */}
      {!loading && focuses.length > 0 && (
        <section className="bg-[#f7f7f7] border-y border-[#9ac7f7]/25">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">
            <div className="flex items-center gap-2 overflow-x-auto py-3 no-scrollbar" data-testid="focus-tabs">
              <button
                onClick={() => setFocus("all")}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-[4px] px-3.5 py-2 text-xs font-display font-bold uppercase tracking-wider transition-colors ${isAll ? "bg-[#04273d] text-white" : "bg-white text-[#04273d] border border-[#9ac7f7]/50 hover:border-[#0d9b80]"}`}
                data-testid="focus-tab-all"
              >
                <LayoutGrid size={13} /> All focuses
              </button>
              {focuses.map((f) => (
                <button
                  key={f.slug}
                  onClick={() => setFocus(f.slug)}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-[4px] px-3.5 py-2 text-xs font-display font-bold uppercase tracking-wider transition-colors ${activeFocus === f.slug ? "bg-[#04273d] text-white" : "bg-white text-[#04273d] border border-[#9ac7f7]/50 hover:border-[#0d9b80]"}`}
                  data-testid={`focus-tab-${f.slug}`}
                >
                  {f.label}
                  <span className={`text-[10px] font-mono-brand ${activeFocus === f.slug ? "text-[#71edc9]" : "text-[#447694]"}`}>{f.items.length}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 grid lg:grid-cols-12 gap-8">
          {/* MAIN COLUMN */}
          <div className="lg:col-span-8 space-y-10">
            {loading && (
              <div className="bnp-card p-12 text-center text-[#447694]" data-testid="insights-loading">Loading articles…</div>
            )}

            {!loading && items.length === 0 && (
              <div className="bnp-card p-12 text-center text-[#447694]" data-testid="insights-empty">
                No articles published yet. Check back soon.
              </div>
            )}

            {/* ALL FOCUSES - featured + grouped-by-focus headers */}
            {!loading && isAll && featured && (
              <Link to={`/insights/${featured.slug}`} className="bnp-card overflow-hidden grid md:grid-cols-2 group" data-testid="insights-featured-card">
                <div className="relative h-56 md:h-auto bg-[#04273d] overflow-hidden">
                  {featured.cover_image && <img src={featured.cover_image} alt="" className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform" />}
                  <div className="absolute top-3 left-3 bnp-chip bnp-chip-navy">Featured</div>
                </div>
                <div className="p-6 md:p-8 flex flex-col">
                  <span className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">{focusLabel(featured.category)} · {featured.read_minutes} min read</span>
                  <h2 className="font-display text-2xl md:text-3xl text-[#04273d] mt-2 font-semibold tracking-tight">{featured.title}</h2>
                  <p className="text-[#447694] text-sm mt-3 leading-relaxed">{featured.excerpt}</p>
                  <span className="text-[#04273d] font-medium text-sm mt-5 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Read article <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            )}

            {!loading && isAll && focuses.map((f) => {
              const list = f.items.filter((p) => p.id !== featured?.id);
              if (list.length === 0) return null;
              const preview = list.slice(0, 4);
              return (
                <div key={f.slug} data-testid={`focus-section-${f.slug}`}>
                  <button
                    onClick={() => setFocus(f.slug)}
                    className="group flex items-center justify-between w-full text-left border-b border-[#9ac7f7]/30 pb-3 mb-5"
                    data-testid={`focus-header-${f.slug}`}
                  >
                    <h2 className="font-display text-xl md:text-2xl text-[#04273d] font-semibold tracking-tight flex items-center gap-2.5">
                      {f.label}
                      <span className="font-mono-brand text-[11px] text-[#447694] font-medium">{f.items.length} {f.items.length === 1 ? "article" : "articles"}</span>
                    </h2>
                    <span className="text-[#0d9b80] font-display font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap">
                      View all <ArrowRight size={14} />
                    </span>
                  </button>
                  <div className="grid sm:grid-cols-2 gap-5">
                    {preview.map((p) => (
                      <ArticleCard key={p.id} p={p} testid={`insights-card-${p.slug}`} />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* SINGLE FOCUS VIEW */}
            {!loading && !isAll && activeGroup && (
              <div data-testid={`focus-view-${activeGroup.slug}`}>
                <button onClick={() => setFocus("all")} className="inline-flex items-center gap-1 text-sm text-[#447694] hover:text-[#04273d] mb-5" data-testid="focus-back-all">
                  <ArrowLeft size={14} /> All focuses
                </button>
                <div className="flex items-center justify-between border-b border-[#9ac7f7]/30 pb-3 mb-6">
                  <h2 className="font-display text-2xl md:text-3xl text-[#04273d] font-semibold tracking-tight">{activeGroup.label}</h2>
                  <span className="font-mono-brand text-[11px] text-[#447694] uppercase tracking-widest">{activeGroup.items.length} {activeGroup.items.length === 1 ? "article" : "articles"}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  {activeGroup.items.map((p) => (
                    <ArticleCard key={p.id} p={p} testid={`insights-card-${p.slug}`} />
                  ))}
                </div>
              </div>
            )}

            {!loading && !isAll && !activeGroup && (
              <div className="bnp-card p-12 text-center text-[#447694]" data-testid="focus-not-found">
                No articles in this focus yet.
                <div className="mt-4">
                  <button onClick={() => setFocus("all")} className="btn-secondary inline-flex text-sm">View all focuses</button>
                </div>
              </div>
            )}
          </div>

          {/* SIDEBAR */}
          <aside className="lg:col-span-4 space-y-5">
            {!loading && focuses.length > 0 && (
              <div className="bnp-card p-6" data-testid="insights-browse-focus-card">
                <LayoutGrid size={20} className="text-[#71edc9]" />
                <h3 className="font-display text-lg text-[#04273d] mt-3 font-semibold">Browse by focus</h3>
                <div className="mt-3 flex flex-col divide-y divide-[#9ac7f7]/20">
                  <button
                    onClick={() => setFocus("all")}
                    className={`flex items-center justify-between py-2.5 text-sm text-left transition-colors ${isAll ? "text-[#0d9b80] font-semibold" : "text-[#04273d] hover:text-[#0d9b80]"}`}
                    data-testid="focus-side-all"
                  >
                    <span>All focuses</span>
                    <span className="font-mono-brand text-[11px] text-[#447694]">{items.length}</span>
                  </button>
                  {focuses.map((f) => (
                    <button
                      key={f.slug}
                      onClick={() => setFocus(f.slug)}
                      className={`flex items-center justify-between py-2.5 text-sm text-left transition-colors ${activeFocus === f.slug ? "text-[#0d9b80] font-semibold" : "text-[#04273d] hover:text-[#0d9b80]"}`}
                      data-testid={`focus-side-${f.slug}`}
                    >
                      <span>{f.label}</span>
                      <span className="font-mono-brand text-[11px] text-[#447694]">{f.items.length}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="bnp-card p-6 sticky top-28" data-testid="insights-newsletter-card">
              <Mail size={20} className="text-[#71edc9]" />
              <h3 className="font-display text-lg text-[#04273d] mt-3 font-semibold">
                <EditableText keyName="insights.newsletter.title" default="Monthly Newsletter and Regular IP Insights" />
              </h3>
              <p className="text-sm text-[#447694] mt-2">
                <EditableText keyName="insights.newsletter.body" default="Original guides, helpful tips and the latest IP news. One email a month. No spam." />
              </p>
              <form onSubmit={subscribe} className="mt-4 space-y-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@yourbusiness.co.uk"
                  className="bnp-input"
                  data-testid="insights-newsletter-email-input"
                />
                <button type="submit" disabled={busy} className="btn-primary w-full justify-center text-sm" data-testid="insights-newsletter-submit-button">
                  {busy ? "..." : "Subscribe"}
                </button>
              </form>
            </div>

            <div className="bnp-card p-6">
              <BookOpen size={20} className="text-[#71edc9]" />
              <h3 className="font-display text-lg text-[#04273d] mt-3 font-semibold">Start with a free check</h3>
              <p className="text-sm text-[#447694] mt-2">See your name's risk profile in 30 seconds.</p>
              <Link to="/check" className="btn-secondary w-full justify-center mt-4 text-sm">
                Check a name <ArrowRight size={14} />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
